<template>
  <div class="frisbuy-mobile-video-controls">
    <div class="frisbuy-mobile-video-controls__top">
      <button
        type="button"
        class="frisbuy-mobile-video-controls__btn frisbuy-mobile-video-controls__btn--play"
        aria-label="Play / pause"
        @click="togglePlay"
      >
        <svg v-if="isPlaying" width="12" height="12" viewBox="0 0 12 12" fill="none">
          <rect x="1" y="1" width="2.4" height="8" rx="0.8" fill="white" />
          <rect x="6.2" y="1" width="2.4" height="8" rx="0.8" fill="white" />
        </svg>
        <svg v-else width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M11.2 7L3.2 11.5V2.5L11.2 7Z" fill="white" />
        </svg>
      </button>

      <span class="frisbuy-mobile-video-controls__time">
        {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
      </span>

      <div class="frisbuy-mobile-video-controls__spacer"></div>

      <button
        type="button"
        class="frisbuy-mobile-video-controls__btn"
        aria-label="Mute / unmute"
        @click="toggleMute"
      >
        <svg v-if="isMuted" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            d="M3.5 8H6.5L10 5.5V14.5L6.5 12H3.5V8Z"
            stroke="white"
            stroke-width="1.8"
            stroke-linejoin="round"
          />
          <path
            d="M12.8 8L16 11.2"
            stroke="white"
            stroke-width="1.8"
            stroke-linecap="round"
          />
          <path
            d="M16 8L12.8 11.2"
            stroke="white"
            stroke-width="1.8"
            stroke-linecap="round"
          />
        </svg>
        <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            d="M3.5 8H6.5L10 5.5V14.5L6.5 12H3.5V8Z"
            stroke="white"
            stroke-width="1.8"
            stroke-linejoin="round"
          />
          <path
            d="M12.6 8.1C13.5 9 13.5 10.5 12.6 11.4"
            stroke="white"
            stroke-width="1.8"
            stroke-linecap="round"
          />
          <path
            d="M14.8 6.4C16.7 8.3 16.7 11.3 14.8 13.2"
            stroke="white"
            stroke-width="1.8"
            stroke-linecap="round"
          />
        </svg>
      </button>

      <button
        type="button"
        class="frisbuy-mobile-video-controls__btn"
        aria-label="Fullscreen"
        @click="toggleFullscreen"
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path d="M2.8 7V2.8H7" stroke="white" stroke-width="1.6" stroke-linecap="round"/>
          <path d="M11 2.8H15.2V7" stroke="white" stroke-width="1.6" stroke-linecap="round"/>
          <path d="M15.2 11V15.2H11" stroke="white" stroke-width="1.6" stroke-linecap="round"/>
          <path d="M7 15.2H2.8V11" stroke="white" stroke-width="1.6" stroke-linecap="round"/>
        </svg>
      </button>

      <div ref="speedMenuWrapRef" class="frisbuy-mobile-video-controls__menu-wrap">
        <button
          type="button"
          class="frisbuy-mobile-video-controls__btn frisbuy-mobile-video-controls__btn--dots"
          aria-label="More"
          aria-haspopup="menu"
          :aria-expanded="isSpeedMenuOpen"
          @click="toggleSpeedMenu"
        >
          <span></span><span></span><span></span>
        </button>

        <PopupReviewMobileVideoSpeedMenu
          v-if="isSpeedMenuOpen"
          :modelValue="playbackRate"
          @update:modelValue="setPlaybackRate"
          @close="isSpeedMenuOpen = false"
        />
      </div>
    </div>

    <input
      class="frisbuy-mobile-video-controls__progress"
      type="range"
      min="0"
      :max="duration || 0"
      step="0.01"
      :value="currentTime"
      @input="seek"
      aria-label="Seek"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import PopupReviewMobileVideoSpeedMenu from "./PopupReviewMobileVideoSpeedMenu.vue";

const props = defineProps<{
  videoEl: HTMLVideoElement | null;
}>();

const video = computed(() => props.videoEl);
const currentTime = ref(0);
const duration = ref(0);
const isPlaying = ref(false);
const isMuted = ref(false);
const playbackRate = ref(1);
const isSpeedMenuOpen = ref(false);
const speedMenuWrapRef = ref<HTMLElement | null>(null);

function sync() {
  if (!video.value) return;
  currentTime.value = video.value.currentTime || 0;
  duration.value = Number.isFinite(video.value.duration) ? video.value.duration : 0;
  isPlaying.value = !video.value.paused;
  isMuted.value = video.value.muted || video.value.volume === 0;
  playbackRate.value = Number.isFinite(video.value.playbackRate) ? video.value.playbackRate : 1;
}

function onTimeUpdate() {
  if (!video.value) return;
  currentTime.value = video.value.currentTime || 0;
}

function onDurationChange() {
  if (!video.value) return;
  duration.value = Number.isFinite(video.value.duration) ? video.value.duration : 0;
}

function onPlay() {
  isPlaying.value = true;
}

function onPause() {
  isPlaying.value = false;
}

function onVolumeChange() {
  if (!video.value) return;
  isMuted.value = video.value.muted || video.value.volume === 0;
}

function bind(el: HTMLVideoElement | null, prev?: HTMLVideoElement | null) {
  if (prev) {
    prev.removeEventListener("timeupdate", onTimeUpdate);
    prev.removeEventListener("durationchange", onDurationChange);
    prev.removeEventListener("play", onPlay);
    prev.removeEventListener("pause", onPause);
    prev.removeEventListener("volumechange", onVolumeChange);
  }

  if (!el) return;

  el.addEventListener("timeupdate", onTimeUpdate);
  el.addEventListener("durationchange", onDurationChange);
  el.addEventListener("play", onPlay);
  el.addEventListener("pause", onPause);
  el.addEventListener("volumechange", onVolumeChange);
  sync();
}

watch(video, (el, prev) => bind(el, prev), { immediate: true });

onBeforeUnmount(() => {
  bind(null, video.value || undefined);
  document.removeEventListener("pointerdown", onDocumentPointerDown);
  document.removeEventListener("keydown", onDocumentKeydown);
});

onMounted(() => {
  document.addEventListener("pointerdown", onDocumentPointerDown);
  document.addEventListener("keydown", onDocumentKeydown);
});

function togglePlay() {
  if (!video.value) return;
  if (video.value.paused) {
    video.value.play();
    return;
  }
  video.value.pause();
}

function toggleMute() {
  if (!video.value) return;
  video.value.muted = !video.value.muted;
  isMuted.value = video.value.muted;
}

function toggleFullscreen() {
  if (!video.value) return;

  if (document.fullscreenElement) {
    document.exitFullscreen();
    return;
  }

  const parent = video.value.parentElement;
  const target = parent || video.value;
  const anyTarget = target as HTMLElement & {
    webkitRequestFullscreen?: () => void;
  };
  const anyVideo = video.value as HTMLVideoElement & {
    webkitEnterFullscreen?: () => void;
  };

  if (target.requestFullscreen) {
    target.requestFullscreen();
  } else if (anyTarget.webkitRequestFullscreen) {
    anyTarget.webkitRequestFullscreen();
  } else if (anyVideo.webkitEnterFullscreen) {
    anyVideo.webkitEnterFullscreen();
  }
}

function seek(event: Event) {
  if (!video.value) return;
  const target = event.target as HTMLInputElement;
  video.value.currentTime = Number(target.value);
  currentTime.value = video.value.currentTime;
}

function toggleSpeedMenu() {
  isSpeedMenuOpen.value = !isSpeedMenuOpen.value;
}

function setPlaybackRate(rate: number) {
  playbackRate.value = rate;
  if (!video.value) return;
  video.value.playbackRate = rate;
}

function onDocumentPointerDown(event: PointerEvent) {
  if (!isSpeedMenuOpen.value) return;
  const target = event.target as Node | null;
  if (!target) return;
  if (speedMenuWrapRef.value?.contains(target)) return;
  isSpeedMenuOpen.value = false;
}

function onDocumentKeydown(event: KeyboardEvent) {
  if (event.key !== "Escape") return;
  isSpeedMenuOpen.value = false;
}

function formatTime(time: number) {
  const safe = Number.isFinite(time) ? Math.max(0, Math.floor(time)) : 0;
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}
</script>

<style scoped lang="scss">
.frisbuy-mobile-video-controls {
  box-sizing: border-box;
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 6;
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 67px;
  padding: 10px 14px;
  background: transparent;
}

.frisbuy-mobile-video-controls__top {
  display: flex;
  align-items: center;
  gap: 14px;
}

.frisbuy-mobile-video-controls__spacer {
  flex: 1 1 auto;
}

.frisbuy-mobile-video-controls__btn {
  width: 20px;
  height: 20px;
  border: 0;
  padding: 0;
  background: transparent;
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.frisbuy-mobile-video-controls__btn--play {
  width: 20px;
  height: 20px;

  svg {
    transform: scale(1.08);
    transform-origin: center;
  }
}

.frisbuy-mobile-video-controls__btn--dots {
  width: 14px;
  height: 14px;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  line-height: 0;
  transform: translateY(-0.5px);

  span {
    display: block;
    width: 2px;
    height: 2px;
    border-radius: 50%;
    background: #ffffff;
  }
}

.frisbuy-mobile-video-controls__menu-wrap {
  position: relative;
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 20px;
}

.frisbuy-mobile-video-controls__time {
  color: #ffffff;
  font-size: 12px;
  line-height: 1;
  transform-origin: left center;
  margin-right: -30px;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.frisbuy-mobile-video-controls__progress {
  width: 100%;
  margin: auto 0;
  height: 3px;
  background: transparent;
  -webkit-appearance: none;
  appearance: none;
}

.frisbuy-mobile-video-controls__progress::-webkit-slider-runnable-track {
  height: 3px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.95);
}

.frisbuy-mobile-video-controls__progress::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 13px;
  height: 13px;
  margin-top: -5px;
  border: 0;
  border-radius: 50%;
  background: #ffffff;
}

.frisbuy-mobile-video-controls__progress::-moz-range-track {
  height: 3px;
  border: 0;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.95);
}

.frisbuy-mobile-video-controls__progress::-moz-range-thumb {
  width: 13px;
  height: 13px;
  border: 0;
  border-radius: 50%;
  background: #ffffff;
}
</style>
